import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useStateContext } from "contexts/ContextProvider";
import { useTheme, Button, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid2";
import CardDashboardItem from "components/CardDashboardItem";
import CardDashboardKPIChart from "components/CardDashboardKPIChart";
import {
  pieArcLabelClasses,
  ChartsTooltip,
  legendClasses,
  PiePlot,
  ResponsiveChartContainer,
} from "@mui/x-charts";

import CardDashboardItemTripleData from "components/CardDashboardItemHorizontalMulti";
import ColumnFilter from "components/ColumnFilter";
import ColumnDateFilter from "components/ColumnDateFilter";
import DataGridHeader from "components/DataGridHeader";
import useColumnFilters from "hooks/useColumnFilters";
import useAssignmentUpdates from "hooks/useAssignmentUpdates";
import { usePresence } from "contexts/PresenceContext";
import { usePagePresence } from "hooks/useAssignmentPresence";
import {
  DataGrid,
  GridToolbarContainer,
} from "@mui/x-data-grid";
import ClearIcon from "@mui/icons-material/ClearRounded";
import { formatDate } from "helpers/ServiceHelpers";
import api from "services/api";
import CEQuickFilter from "components/CEQuickFilter";
import { OVERLAY_NAMES } from "constants/Overlays";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import SmsRoundedIcon from "@mui/icons-material/SmsRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import AddTaskRoundedIcon from "@mui/icons-material/AddTaskRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { ListItemIcon, Typography, Divider } from "@mui/material";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { EditRounded } from "@mui/icons-material";

const DashboardAllClients = ({ user, dateStart, dateEnd }) => {
  const { refreshPage, setOverlayData, setOverlayName, showSnackbar } =
    useStateContext();
  const theme = useTheme();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);

  const { handleErrorDialog } = useStateContext();

  const [clientsKPIs, setClientsKPIs] = useState();
  const [listOfClients, setListOfClients] = useState();

  const [listofClientsTypes, setListOfClientsTypes] = useState([]);
  const [listofClientServiceTypes, setListOfClientServiceTypes] = useState([]);
  const [listofContractStatuses, setListOfContractStatuses] = useState([]);

  const [filterBy, setFilterBy] = useState(null);

  const [title, setTitle] = useState("All Clients");

  const [contextMenu, setContextMenu] = useState(null);

  const [selectedRow, setSelectedRow] = React.useState();

  // Ref for DataGrid container debug
  const dataGridContainerRef = useRef(null);

  // Set up entity tracking for ClientsList to receive real-time updates
  const { setCurrentEntity } = usePresence();
  usePagePresence();

  useEffect(() => {
    // Set entity to "ClientsList:all" so socket joins the entity room for list-level updates
    setCurrentEntity({
      entityType: "ClientsList",
      entityId: "all",
    });

    return () => {
      // Clean up entity tracking when component unmounts
      setCurrentEntity({ entityType: null, entityId: null });
    };
  }, [setCurrentEntity]);

  // Debug logging for DataGrid height
  useEffect(() => {
    const logDimensions = () => {
      if (dataGridContainerRef.current) {
        dataGridContainerRef.current.getBoundingClientRect();
      }
    };

    // Log on mount and on resize
    logDimensions();
    window.addEventListener("resize", logDimensions);

    return () => window.removeEventListener("resize", logDimensions);
  }, []);

  const handleContextMenu = (event) => {
    event.preventDefault();

    setSelectedRow(Number(event.currentTarget.getAttribute("data-id")));
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
          // Other native context menus might behave different.
          // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
          null,
    );
  };

  const handleContextMenuEdit = () => {
    if (!listOfClients) return;

    const secondData = {
      ClientID: selectedRow,
      Client: listOfClients.find((linguist) => linguist.id === selectedRow),
      ClientTypes: listofClientsTypes,
      ContractStatuses: listofContractStatuses,
      ClientServiceTypes: listofClientServiceTypes,
      Action: OVERLAY_NAMES.CLIENT_ACTION_EDIT,
    };

    setOverlayData(secondData);

    setOverlayName(OVERLAY_NAMES.CLIENT_NEW_EDIT);

    setContextMenu(null);
  };

  const getMainRequestorPhoneNumber = (requestors) => {
    if (!requestors || requestors.length === 0) return null;

    const mainRequestor = requestors.find((requestor) => requestor.IsMain);

    if (mainRequestor) {
      return mainRequestor.PhoneNumber;
    }

    if (requestors.length === 1) {
      return requestors[0].PhoneNumber;
    }

    return null;
  };

  const handleContextMenuCall = () => {
    if (!listOfClients) return;

    const selectedClient = listOfClients.find(
      (client) => client.id === selectedRow,
    );
    if (selectedClient) {
      const phoneNumber = getMainRequestorPhoneNumber(
        selectedClient.Requestors,
      );
      if (phoneNumber) {
        window.location.href = `tel:${phoneNumber}`;
      } else {
        showSnackbar(
          "No Phone Number Available for this Client.",
          theme,
          "error",
        );
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuSendEmail = () => {
    if (!listOfClients) return;

    const selectedLinguist = listOfClients.find(
      (client) => client.id === selectedRow,
    );
    if (selectedLinguist) {
      const email = selectedLinguist.Email;
      if (email) {
        window.open(`mailto:${email}`, "_blank");
      } else {
        showSnackbar("No Email Available for this Client.", theme, "error");
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuSendSMS = () => {
    if (!listOfClients) return;

    const selectedClient = listOfClients.find(
      (client) => client.id === selectedRow,
    );
    if (selectedClient) {
      const phoneNumber = getMainRequestorPhoneNumber(
        selectedClient.Requestors,
      );
      if (phoneNumber) {
        window.location.href = `sms:${phoneNumber}`;
      } else {
        showSnackbar(
          "No Phone Number Available for this Client.",
          theme,
          "error",
        );
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuOpenMaps = () => {
    if (!listOfClients) return;

    const client = listOfClients.find(({ id }) => id === selectedRow);

    if (client) {
      if (client.MainAddress) {
        const encodedAddress = encodeURIComponent(client.MainAddress);
        window.open(
          `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`,
          "_blank",
        );
      } else {
        showSnackbar("No Address Available for this Client.", theme, "error");
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuCopyEmail = () => {
    if (!listOfClients) return;

    const client = listOfClients.find(({ id }) => id === selectedRow);
    if (client && client.Requestors && client.Requestors.length > 0) {
      const email = client.Requestors[0].Email;
      if (email) {
        navigator.clipboard
          .writeText(email)
          .then(() =>
            showSnackbar(
              "Email successfully copied to clipboard.",
              theme,
              "success",
            ),
          )
          .catch(() =>
            showSnackbar("Failed to copy email to clipboard.", theme, "error"),
          );
      } else {
        showSnackbar("No Email Available for this Client.", theme, "error");
      }
    } else {
      showSnackbar("No Email Available for this Client.", theme, "error");
    }
    setContextMenu(null);
  };

  const handleContextMenuCopyNumber = () => {
    if (!listOfClients) return;

    const selectedClient = listOfClients.find(
      (client) => client.id === selectedRow,
    );
    if (
      selectedClient &&
      selectedClient.Requestors &&
      selectedClient.Requestors.length > 0
    ) {
      const phoneNumber = getMainRequestorPhoneNumber(
        selectedClient.Requestors,
      );
      if (phoneNumber) {
        navigator.clipboard
          .writeText(phoneNumber)
          .then(() => {
            showSnackbar(
              "Phone number successfully copied to clipboard.",
              theme,
              "success",
            );
          })
          .catch(() => {
            showSnackbar(
              "Failed to copy phone number to clipboard.",
              theme,
              "error",
            );
          });
      } else {
        showSnackbar(
          "No Phone Number Available for this Client.",
          theme,
          "error",
        );
      }
    } else {
      showSnackbar(
        "No Phone Number Available for this Client.",
        theme,
        "error",
      );
    }
    setContextMenu(null);
  };

  const handleContextMenuCopyAddress = () => {
    if (!listOfClients) return;

    const client = listOfClients.find(({ id }) => id === selectedRow);
    if (client) {
      const address = client.MainAddress;
      if (address) {
        navigator.clipboard
          .writeText(address)
          .then(() =>
            showSnackbar(
              "Address successfully copied to clipboard.",
              theme,
              "success",
            ),
          )
          .catch(() =>
            showSnackbar(
              "Failed to copy address to clipboard.",
              theme,
              "error",
            ),
          );
      } else {
        showSnackbar("No Address Available for this Client.", theme, "error");
      }
    } else {
      showSnackbar("No Address Available for this Client.", theme, "error");
    }
    setContextMenu(null);
  };

  const handleContextMenuAddTask = () => {
    if (!listOfClients) return;

    const clientData = listOfClients.find(({ id }) => id === selectedRow);
    if (!clientData) return;

    setOverlayData({
      Action: OVERLAY_NAMES.TASK_ACTION_NEW,
      Task: {
        EntityType: "Client",
        EntityID: selectedRow,
      },
      Client: {
        id: clientData.id,
        Account: clientData.Account,
      },
    });
    setOverlayName(OVERLAY_NAMES.TASK_NEW_EDIT);
    setContextMenu(null);
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  const getSelectedClientData = () => {
    if (!listOfClients) return null;
    return listOfClients.find(({ id }) => id === selectedRow);
  };

  const hasEmail = () => {
    const client = getSelectedClientData();
    return client?.Requestors?.[0]?.Email;
  };

  const hasPhoneNumber = () => {
    const client = getSelectedClientData();
    return getMainRequestorPhoneNumber(client?.Requestors);
  };

  const hasAddress = () => {
    const client = getSelectedClientData();
    return client?.MainAddress;
  };

  // Fetch clients dashboard data
  const fetchClientsData = useCallback(() => {
    api
      .all([
        api.get("/dashboard/all/clients/clientTotals"),
        api.get("/dashboard/all/clients/clients", {
          params: filterBy,
        }),
      ])
      .then(
        api.spread((clientsKPIsResponse, clientsResponse) => {
          if (clientsKPIsResponse) {
            setClientsKPIs(clientsKPIsResponse.data);
          }
          if (clientsResponse) {
            setListOfClients(clientsResponse.data);
          }

          // Set loading to false after all data is loaded
          setIsLoading(false);
        }),
      )
      .catch((error) => {
        const message =
          error.response?.data?.message ||
          "Unable to connect to the server. Please try again later.";
        handleErrorDialog(message, theme);

        setIsLoading(false); // Make sure to set loading to false on error
      });
  }, [filterBy, handleErrorDialog, theme]);

  // Fetch static data for context menu (client types, contract statuses, service types) - only on mount
  useEffect(() => {
    api.get("/clients/clientTypes").then((response) => {
      if (response !== null) setListOfClientsTypes(response.data);
    });

    api.get("/clients/contractStatuses").then((response) => {
      if (response !== null) setListOfContractStatuses(response.data);
    });

    api.get("/clients/clientServiceTypes").then((response) => {
      if (response !== null) setListOfClientServiceTypes(response.data);
    });
  }, []);

  useEffect(() => {
    fetchClientsData();
  }, [refreshPage, fetchClientsData]);

  // Listen for client updates from other members
  const handleDashboardClientsRefetch = useCallback(
    (updatedBy) => {
      // Reload the clients dashboard data
      fetchClientsData();
    },
    [fetchClientsData],
  );

  useAssignmentUpdates({
    modelType: "ClientsList",
    recordId: "all",
    onUpdate: handleDashboardClientsRefetch,
  });

  const clientsByTypePieData = [
    {
      id: 0,
      value: clientsKPIs?.byType?.Customer || "",
      label: "Customer",
      color: theme.palette.primary.main,
    },
    {
      id: 1,
      value: clientsKPIs?.byType?.Prospect || "",
      label: "Prospect",
      color: theme.palette.info[500],
    },
    {
      id: 2,
      value: clientsKPIs?.byType?.Former || "",
      label: "Former",
      color: theme.palette.info[400],
    },
    {
      id: 3,
      value: clientsKPIs?.byType["N/A"] || "",
      label: "N/A",
      color: theme.palette.error.main,
    },
  ];

  const clientsByServiceTypePieData = [
    {
      id: 0,
      value: clientsKPIs?.byServiceType["OPI/VRI"] || "",
      label: "OPI/VRI",
      color: theme.palette.primary.main,
    },
    {
      id: 1,
      value: clientsKPIs?.byServiceType.Translation || "",
      label: "Translation",
      color: theme.palette.info[500],
    },
    {
      id: 2,
      value: clientsKPIs?.byServiceType["In-Person Interpretation"] || "",
      label: "In-Person Interpretation",
      color: theme.palette.info[400],
    },
    {
      id: 3,
      value: clientsKPIs?.byServiceType.Training || "",
      label: "Training",
      color: theme.palette.warning[400],
    },
    {
      id: 4,
      value: clientsKPIs?.byServiceType.Consulting || "",
      label: "Consulting",
      color: theme.palette.warning[400],
    },
    {
      id: 5,
      value: clientsKPIs?.byServiceType["N/A"] || "",
      label: "N/A",
      color: theme.palette.error.main,
    },
  ];

  const clientsByContractPieData = [
    {
      id: 0,
      value: clientsKPIs?.byContractStatus.Current || "",
      label: "Current",
      color: theme.palette.primary.main,
    },
    {
      id: 1,
      value: clientsKPIs?.byContractStatus["Needs Contract"] || "",
      label: "Needs Contract",
      color: theme.palette.error[400],
    },
    {
      id: 2,
      value: clientsKPIs?.byContractStatus["N/A"] || "",
      label: "N/A",
      color: theme.palette.warning[400],
    },
  ];

  const updateFilterBy = (data) => {
    const tempFilterBy = data.seriesId;
    let filterValue = "";
    let titleFilterBy = "Type";

    switch (tempFilterBy) {
      case "Contract":
        titleFilterBy = "Contract Status";
        switch (data.dataIndex) {
          case 0:
            filterValue = "Current";
            break;
          case 1:
            filterValue = "Needs Contract";
            break;
          case 2:
            filterValue = "N/A";
            break;
          default:
            break;
        }
        break;
      case "ServiceType":
        titleFilterBy = "Service Type";
        switch (data.dataIndex) {
          case 0:
            filterValue = "OPI/VRI";
            break;
          case 1:
            filterValue = "Translation";
            break;
          case 2:
            filterValue = "In-Person Interpretation";
            break;
          case 3:
            filterValue = "Training";
            break;
          case 4:
            filterValue = "Consulting";
            break;
          case 5:
            filterValue = "N/A";
            break;
          default:
            break;
        }
        break;
      case "Type":
        switch (data.dataIndex) {
          case 0:
            filterValue = "Customer";
            break;
          case 1:
            filterValue = "Prospect";
            break;
          case 2:
            filterValue = "Former";
            break;
          case 3:
            filterValue = "N/A";
            break;
          default:
            break;
        }
        break;
      default:
        break;
    }

    setFilterBy({
      category: tempFilterBy,
      value: filterValue,
    });

    setTitle(
      "Showing All Clients with a " +
        titleFilterBy +
        " of: " +
        filterValue +
        ":",
    );
  };

  const getPie = (data, id) => {
    const TOTAL = data.map((item) => item.value).reduce((a, b) => a + b, 0);

    const getArcLabel = (params) => {
      const percent = params.value / TOTAL;
      return `${(percent * 100).toFixed(0)}%`;
    };

    return (
      <ResponsiveChartContainer
        series={[
          {
            type: "pie",
            data: data,
            id: id,
            highlightScope: { faded: "global", highlighted: "item" },
            animated: true,
            arcLabel: getArcLabel,
            arcLabelMinAngle: 20,
            innerRadius: 50,
            outerRadius: 100,
            paddingAngle: 2,
            cornerRadius: 10,
          },
        ]}
        margin={{ top: 100, bottom: 100, left: 0, right: 0 }}
        sx={{
          "& path": {
            stroke: "transparent", // set the outline to yellow
            strokeWidth: 5, // increase the stroke width to show the border
          },
          [`& .${pieArcLabelClasses.root}`]: {
            fill: theme.palette.background.default,
            fontSize: 14,
            fontWeight: "bold",
          },
          [`& .${legendClasses.mark}`]: {
            ry: 6,
          },
        }}
      >
        <ChartsTooltip
          trigger="item"
          labelFormatter={(itemData) => {
            return itemData.label || "";
          }}
          valueFormatter={(value, itemData) => {
            return `${value}${itemData.format === "percentage" ? "%" : ""}`;
          }}
        />

        <PiePlot onItemClick={(event, d) => updateFilterBy(d)} />
      </ResponsiveChartContainer>
    );
  };

  const columns = [
    {
      field: "Account",
      headerName: "Account",
      minWidth: 170,
      flex: 1.7,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[0] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "MainAddress",
      headerName: "Address",
      minWidth: 270,
      flex: 2.7,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[1] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Type",
      headerName: "Type",
      minWidth: 150,
      flex: 1.5,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[2] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "ClientContractHistories.Status",
      headerName: "Contract Status",
      valueGetter: (_, params) => {
        const data = params.ClientContractHistories;
        const value =
          data && data[0]
            ? data[0].Status != null
              ? data[0].Status
              : "N/A"
            : "N/A";
        return value;
      },
      minWidth: 170,
      flex: 1.7,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[3] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "ClientContractHistories.Date",
      headerName: "Contract Date",
      valueGetter: (_, params) => {
        const data = params.ClientContractHistories;
        const value =
          data && data[0]
            ? data[0].Date != null
              ? formatDate(data[0].Date)
              : "N/A"
            : "N/A";
        return value;
      },
      minWidth: 150,
      flex: 1.5,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnDateFilter
            data={listOfClients || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Note",
      headerName: "Note",
      minWidth: 200,
      flex: 2,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[4] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "ServiceType.In-Person",
      headerName: "In-Person",
      valueGetter: (_, params) => {
        const data = params.ClientServiceTypes;
        const value =
          data && data.some((item) => item.ServiceType.id === 1) ? "Yes" : "No";
        return value;
      },
      renderCell: (params) => {
        const hasService = params.row.ClientServiceTypes?.some(
          (item) => item.ServiceType.id === 1,
        );
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            {hasService ? (
              <CheckRoundedIcon color="success" sx={{ fontSize: "20px" }} />
            ) : (
              <CloseRoundedIcon color="error" sx={{ fontSize: "20px" }} />
            )}
          </Box>
        );
      },
      minWidth: 130,
      flex: 1.3,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[5] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "ServiceType.OPI",
      headerName: "OPI",
      valueGetter: (_, params) => {
        const data = params.ClientServiceTypes;
        const value =
          data && data.some((item) => item.ServiceType.id === 2) ? "Yes" : "No";
        return value;
      },
      renderCell: (params) => {
        const hasService = params.row.ClientServiceTypes?.some(
          (item) => item.ServiceType.id === 2,
        );
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            {hasService ? (
              <CheckRoundedIcon color="success" sx={{ fontSize: "20px" }} />
            ) : (
              <CloseRoundedIcon color="error" sx={{ fontSize: "20px" }} />
            )}
          </Box>
        );
      },
      minWidth: 100,
      flex: 1,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[6] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "ServiceType.Training",
      headerName: "Training",
      valueGetter: (_, params) => {
        const data = params.ClientServiceTypes;
        const value =
          data && data.some((item) => item.ServiceType.id === 3) ? "Yes" : "No";
        return value;
      },
      renderCell: (params) => {
        const hasService = params.row.ClientServiceTypes?.some(
          (item) => item.ServiceType.id === 3,
        );
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            {hasService ? (
              <CheckRoundedIcon color="success" sx={{ fontSize: "20px" }} />
            ) : (
              <CloseRoundedIcon color="error" sx={{ fontSize: "20px" }} />
            )}
          </Box>
        );
      },
      minWidth: 120,
      flex: 1.2,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[7] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "ServiceType.Consulting",
      headerName: "Consulting",
      valueGetter: (_, params) => {
        const data = params.ClientServiceTypes;
        const value =
          data && data.some((item) => item.ServiceType.id === 4) ? "Yes" : "No";
        return value;
      },
      renderCell: (params) => {
        const hasService = params.row.ClientServiceTypes?.some(
          (item) => item.ServiceType.id === 4,
        );
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            {hasService ? (
              <CheckRoundedIcon color="success" sx={{ fontSize: "20px" }} />
            ) : (
              <CloseRoundedIcon color="error" sx={{ fontSize: "20px" }} />
            )}
          </Box>
        );
      },
      minWidth: 130,
      flex: 1.3,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[8] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "ServiceType.Translation",
      headerName: "Translation",
      valueGetter: (_, params) => {
        const data = params.ClientServiceTypes;
        const value =
          data && data.some((item) => item.ServiceType.id === 5) ? "Yes" : "No";
        return value;
      },
      renderCell: (params) => {
        const hasService = params.row.ClientServiceTypes?.some(
          (item) => item.ServiceType.id === 5,
        );
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            {hasService ? (
              <CheckRoundedIcon color="success" sx={{ fontSize: "20px" }} />
            ) : (
              <CloseRoundedIcon color="error" sx={{ fontSize: "20px" }} />
            )}
          </Box>
        );
      },
      minWidth: 135,
      flex: 1.35,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[9] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
  ];

  // Global filter state management
  const [openFilter, setOpenFilter] = useState(null);
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });

  const handleSortModelChange = (newSortModel) => {
    //setSortModel(newSortModel);
  };

  // Column filters
  const {
    filteredData: columnFilteredData,
    columnFilters,
    handleFilterChange: handleColumnFilterChange,
    clearAllFilters,
    hasActiveFilters,
  } = useColumnFilters(listOfClients || [], columns);

  // Wrapped clear all filters that also clears quick search
  const handleClearAllFilters = useCallback(() => {
    clearAllFilters();
    // Clear quick filter
    setFilterModel({ items: [] });
  }, [clearAllFilters]);

  // Check if quick filter has values
  const hasQuickFilter = filterModel?.quickFilterValues?.length > 0;

  // Combined check for any active filters
  const hasAnyActiveFilters = hasActiveFilters || hasQuickFilter;

  // Filter columns configuration for cross-filtering
  const filterColumns = useMemo(
    () => [
      { field: "Account", valueGetter: (row) => row.Account || "N/A" },
      { field: "MainAddress", valueGetter: (row) => row.MainAddress || "N/A" },
      { field: "Type", valueGetter: (row) => row.Type || "N/A" },
      {
        field: "ClientContractHistories.Status",
        valueGetter: (row) => {
          const data = row.ClientContractHistories;
          return data && data[0]
            ? data[0].Status != null
              ? data[0].Status
              : "N/A"
            : "N/A";
        },
      },
      { field: "Note", valueGetter: (row) => row.Note || "N/A" },
      {
        field: "ServiceType.In-Person",
        valueGetter: (row) => {
          const data = row.ClientServiceTypes;
          return data && data.some((item) => item.ServiceType.id === 1)
            ? "Yes"
            : "No";
        },
      },
      {
        field: "ServiceType.OPI",
        valueGetter: (row) => {
          const data = row.ClientServiceTypes;
          return data && data.some((item) => item.ServiceType.id === 2)
            ? "Yes"
            : "No";
        },
      },
      {
        field: "ServiceType.Training",
        valueGetter: (row) => {
          const data = row.ClientServiceTypes;
          return data && data.some((item) => item.ServiceType.id === 3)
            ? "Yes"
            : "No";
        },
      },
      {
        field: "ServiceType.Consulting",
        valueGetter: (row) => {
          const data = row.ClientServiceTypes;
          return data && data.some((item) => item.ServiceType.id === 4)
            ? "Yes"
            : "No";
        },
      },
      {
        field: "ServiceType.Translation",
        valueGetter: (row) => {
          const data = row.ClientServiceTypes;
          return data && data.some((item) => item.ServiceType.id === 5)
            ? "Yes"
            : "No";
        },
      },
    ],
    [],
  );

  // Cross-filtering data calculation
  const filterData = useMemo(() => {
    return filterColumns.map((column, index) => {
      const otherFilters = Object.entries(columnFilters).filter(
        ([field]) => field !== column.field,
      );

      let filteredData;
      if (otherFilters.length === 0) {
        filteredData = listOfClients || [];
      } else {
        filteredData = (listOfClients || []).filter((row) => {
          return otherFilters.every(([field, selectedValues]) => {
            if (!selectedValues || selectedValues.length === 0) return true;

            const filterColumn = filterColumns.find(
              (col) => col.field === field,
            );
            if (!filterColumn) return true;

            const value = filterColumn.valueGetter(row);
            return selectedValues.includes(value);
          });
        });
      }

      // Limit data for columns with too many unique values
      if (
        ["Account", "MainAddress", "Note"].includes(column.field) &&
        filteredData.length > 0
      ) {
        const valueCounts = {};
        filteredData.forEach((row) => {
          const value = column.valueGetter(row);
          valueCounts[value] = (valueCounts[value] || 0) + 1;
        });

        const topValues = Object.entries(valueCounts)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 50)
          .map(([value]) => value);

        return filteredData.filter((row) => {
          const value = column.valueGetter(row);
          return topValues.includes(value);
        });
      }

      return filteredData;
    });
  }, [columnFilters, listOfClients, filterColumns]);

  // keep toolbar identity stable so quick filter input retains focus
  const clearAllFiltersRef = useRef(handleClearAllFilters);
  useEffect(() => {
    clearAllFiltersRef.current = handleClearAllFilters;
  }, [handleClearAllFilters]);

  const handleToolbarClear = useCallback(() => {
    clearAllFiltersRef.current();
  }, []);

  const CustomToolbar = useMemo(() => {
    const Toolbar = ({ hasAnyActiveFilters, onClear, theme, title }) => (
      <GridToolbarContainer
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 1,
        }}
      >
        {title && (
          <Typography
            variant="h5"
            sx={{
              fontWeight: "bold",
              color: theme.palette.primary.text2,
              margin: 0,
              padding: 0,
              alignSelf: "flex-start",
            }}
          >
            {title}
          </Typography>
        )}

        <Box sx={{ flex: 1 }} />

        <Box
          sx={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
          }}
        >
          {hasAnyActiveFilters && (
            <Button
              startIcon={<ClearIcon />}
              onClick={onClear}
              variant="outlined"
              size="small"
              sx={{
                color: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
                "&:hover": {
                  borderColor: theme.palette.primary.buttonHoverSelected,
                  backgroundColor: theme.palette.primary.buttonHoverSelected,
                  color: theme.palette.background.default,
                },
              }}
            >
              Clear Filters
            </Button>
          )}
        </Box>

        <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
          <CEQuickFilter />
        </Box>
      </GridToolbarContainer>
    );

    return Toolbar;
  }, []);

  const getDataGrid = (listOfServices, title) => {
    return (
      <DataGrid
        loading={isLoading}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        pageSizeOptions={[25, 50, 100, { value: -1, label: "All" }]}
        getRowId={(row) => row.id}
        rows={columnFilteredData || []}
        columns={columns}
        sortModel={sortModel}
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
        disableColumnMenu
        disableColumnSorting
        onRowClick={(params, event, details) => {
          const data = {
            ComingFrom: "/dashboard",
            Client: params.row,
            ClientID: params.row.id,
          };

          setOverlayData(data);

          navigate(`/clients/${params.row.id}`, { state: data });
        }}
        getCellClassName={(params) => {
          return params.value === "No" ||
            params.value === "N/A" ||
            params.value === "0%" ||
            params.value === "null"
            ? "notAvailable"
            : "";
        }}
        slots={{ toolbar: CustomToolbar }}
        slotProps={{
          row: {
            onContextMenu: handleContextMenu,
            style: { cursor: "context-menu" },
          },
          loadingOverlay: {
            variant: "skeleton",
            noRowsVariant: "skeleton",
          },
          toolbar: {
            hasAnyActiveFilters,
            onClear: handleToolbarClear,
            theme,
            title,
          },
        }}
        ignoreDiacritics
        sx={{
          backgroundColor: theme.palette.background.default,
          borderRadius: "0px",

          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: "bold",
            color: theme.palette.primary.main,
            textAlign: "left !important",
            justifyContent: "flex-start !important",
          },
          "& .MuiDataGrid-columnHeaderTitleContainer": {
            justifyContent: "flex-start !important",
          },
          "& .MuiTypography-root.MuiTypography-body1": {
            textAlign: "left !important",
          },
          "& .MuiDataGrid-columnSeparator": {
            color: theme.palette.primary.main,
          },
          "& .MuiButton-root": {
            color: theme.palette.primary.main,
          },
          "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
            outline: "none !important",
          },
          "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
            {
              outline: "none !important",
            },
          "& .MuiDataGrid-scrollbar": {
            margin: "0 0 0 0",
          },
        }}
      />
    );
  };

  return (
    <Grid
      container
      spacing={2}
      size={{ xs: 12 }}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflowX: "hidden",
        overflowY: "hidden",
      }}
    >
      <Grid size={{ xs: 12 }} sx={{ flexShrink: 0, px: 1, pt: 1 }}>
        <CardDashboardItemTripleData
          titles={["Total Clients", "Total Requestors", "Total Sub-Accounts"]}
          contents={[
            clientsKPIs?.totalClients,
            clientsKPIs?.totalRequestors,
            clientsKPIs?.totalSubAccounts,
          ]}
          isLoading={isLoading}
        />
      </Grid>

      <Box
        sx={{
          flex: 1,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflowX: "hidden",
          overflowY: { xs: "auto", md: "hidden" },
          minHeight: 0,
          px: 1,
        }}
      >
        <Grid container spacing={2} sx={{ mb: 2, flex: "0 0 auto" }}>
          <Grid size={{ xs: 12, sm: 12 / 2, md: 12 / 3 }}>
            <CardDashboardKPIChart
              title="Clients By Type"
              formatType="Number"
              thisMonth={null}
              lastMonth={null}
              percentage={null}
              chart={getPie(clientsByTypePieData, "Type")}
              isLoading={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 12 / 2, md: 12 / 3 }}>
            <CardDashboardKPIChart
              title="Clients By Service Type"
              formatType="Number"
              thisMonth={null}
              lastMonth={null}
              percentage={null}
              chart={getPie(clientsByServiceTypePieData, "ServiceType")}
              isLoading={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 12 / 2, md: 12 / 3 }}>
            <CardDashboardKPIChart
              title="Clients By Contract"
              formatType="Number"
              thisMonth={null}
              lastMonth={null}
              percentage={null}
              chart={getPie(clientsByContractPieData, "Contract")}
              isLoading={isLoading}
            />
          </Grid>
        </Grid>

        <Box
          ref={dataGridContainerRef}
          sx={{
            width: "100%",
            minHeight: { xs: 600, md: 0 },
            flex: { xs: "0 0 auto", md: 1 },
            display: { md: "flex" },
            pb: 1,
          }}
        >
          <CardDashboardItem
            title={null}
            content={getDataGrid(listOfClients, title)}
            isLoading={isLoading}
            height={{ xs: 600, md: "100%" }}
          />
        </Box>

        {/* TO DO Take this to the Clients Reports

        <Grid size={{ xs: 12, sm: 12 }}>
          <CardDashboardKPIChart
            title="By Account Charges"
            formatType="Number"
            thisMonth={null}
            lastMonth={null}
            percentage={null}
            chart={getStackedBar(topClients?.byAccountCharge, "currency")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12 }}>
          <CardDashboardKPIChart
            title="By Profit"
            formatType="Number"
            thisMonth={null}
            lastMonth={null}
            percentage={null}
            chart={getStackedBar(topClients?.byProfit, "currency")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12 }}>
          <CardDashboardKPIChart
            title="By Number of Orders"
            formatType="Number"
            thisMonth={null}
            lastMonth={null}
            percentage={null}
            chart={getStackedBar(topClients?.byOrderCount, "currency")}
          />
        </Grid>*/}
      </Box>
      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        slotProps={{
          paper: {
            sx: {
              bgcolor: theme.palette.background.alt,
              border: `1px solid ${theme.palette.divider}`,
              "& .MuiListItemIcon-root": {
                color: theme.palette.primary.main,
              },
            },
          },
        }}
      >
        <MenuItem onClick={handleContextMenuEdit}>
          <ListItemIcon>
            <EditRounded fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">Edit</Typography>
        </MenuItem>
        <MenuItem onClick={handleContextMenuAddTask}>
          <ListItemIcon>
            <AddTaskRoundedIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">Add Task</Typography>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={handleContextMenuCall}
          disabled={!hasPhoneNumber()}
          sx={{
            opacity: hasPhoneNumber() ? 1 : 0.5,
            "&.Mui-disabled": {
              opacity: 0.5,
            },
          }}
        >
          <ListItemIcon>
            <CallRoundedIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">Call</Typography>
        </MenuItem>
        <MenuItem
          onClick={handleContextMenuSendEmail}
          disabled={!hasEmail()}
          sx={{
            opacity: hasEmail() ? 1 : 0.5,
            "&.Mui-disabled": {
              opacity: 0.5,
            },
          }}
        >
          <ListItemIcon>
            <EmailRoundedIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">Send Email</Typography>
        </MenuItem>
        <MenuItem
          onClick={handleContextMenuSendSMS}
          disabled={!hasPhoneNumber()}
          sx={{
            opacity: hasPhoneNumber() ? 1 : 0.5,
            "&.Mui-disabled": {
              opacity: 0.5,
            },
          }}
        >
          <ListItemIcon>
            <SmsRoundedIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">Send SMS</Typography>
        </MenuItem>
        <MenuItem
          onClick={handleContextMenuOpenMaps}
          disabled={!hasAddress()}
          sx={{
            opacity: hasAddress() ? 1 : 0.5,
            "&.Mui-disabled": {
              opacity: 0.5,
            },
          }}
        >
          <ListItemIcon>
            <LocationOnRoundedIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">Open Maps</Typography>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={handleContextMenuCopyEmail}
          disabled={!hasEmail()}
          sx={{
            opacity: hasEmail() ? 1 : 0.5,
            "&.Mui-disabled": {
              opacity: 0.5,
            },
          }}
        >
          <ListItemIcon>
            <ContentCopyRoundedIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">Copy Email</Typography>
        </MenuItem>
        <MenuItem
          onClick={handleContextMenuCopyNumber}
          disabled={!hasPhoneNumber()}
          sx={{
            opacity: hasPhoneNumber() ? 1 : 0.5,
            "&.Mui-disabled": {
              opacity: 0.5,
            },
          }}
        >
          <ListItemIcon>
            <ContentCopyRoundedIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">Copy Number</Typography>
        </MenuItem>
        <MenuItem
          onClick={handleContextMenuCopyAddress}
          disabled={!hasAddress()}
          sx={{
            opacity: hasAddress() ? 1 : 0.5,
            "&.Mui-disabled": {
              opacity: 0.5,
            },
          }}
        >
          <ListItemIcon>
            <ContentCopyRoundedIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">Copy Address</Typography>
        </MenuItem>
      </Menu>
    </Grid>
  );
};

export default DashboardAllClients;
